import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_PHOTOS_PER_SUGGESTION, photosApi, type StopSuggestion } from '../api/photos.api';
import type { TripPhoto } from '../types';
import { useAuthStore } from '../stores/auth.store';
import { toast } from '../components/ui/Toast';

export interface SuggestionReview {
  photos: TripPhoto[];
  /** Null while the AI is still looking. */
  suggestions: StopSuggestion[] | null;
}

/**
 * ADR-0058: asks the AI where the photos were taken, in requests of up to ten
 * (one credit each), and holds the answers for review. Applying is the
 * caller's: the ordinary photo PATCH.
 */
export function usePhotoStopSuggestions(tripId: string) {
  const { t } = useTranslation();
  const { updateAiCredits, updatePremiumAiCredits } = useAuthStore();
  const [review, setReview] = useState<SuggestionReview | null>(null);

  async function suggest(photos: TripPhoto[]): Promise<void> {
    if (photos.length === 0) return;
    setReview({ photos, suggestions: null });
    const suggestions: StopSuggestion[] = [];
    try {
      for (let i = 0; i < photos.length; i += MAX_PHOTOS_PER_SUGGESTION) {
        const chunk = photos.slice(i, i + MAX_PHOTOS_PER_SUGGESTION).map(p => p.id);
        const data = await photosApi.suggestStops(tripId, chunk);
        updateAiCredits(data.aiCreditsLeft);
        updatePremiumAiCredits(data.premiumAiCreditsLeft);
        suggestions.push(...data.suggestions);
      }
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      toast(t(status === 402 ? 'billing.aiPaywall.error' : 'gallery.suggest.failed'), 'error');
      // What the earlier requests already found is still worth reviewing.
      if (suggestions.length === 0) { setReview(null); return; }
    }
    setReview({ photos: photos.filter(p => suggestions.some(s => s.photoId === p.id)), suggestions });
  }

  return { review, suggest, close: () => setReview(null) };
}
